import { observer } from "mobx-react";
import { Modal } from "react-bootstrap";
import { useStores } from "../../stores/root-store";
import {
  ApiResponse,
  DeliveryMethod,
  IModal,
  PaymentMethod,
} from "../../stores/index.d";
import {
  currencyFormatter,
  getDiscountedValue,
  ShowAlert,
} from "../../utils/functions";
import { useCallback, useEffect, useState } from "react";
import Payment from "./payment";
import CartItems from "./cart-items";
import { useAuth } from "../../contexts/auth-context";
import LoginPage from "../account/login";
import AddressSection from "./address";
import { IAddress, IDistanceCheckResponse } from "../../stores/cart";
import { ICreateOrderResponse } from "../../stores/order";
import OrderSuccessBanner from "./order-success-banner";
import { useCookies } from "react-cookie";
import moment from "moment";
import { useShopContext } from "../../contexts/shop-context";
import {
  faUtensils,
  faShoppingBasket,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { useLocation } from "react-router-dom";
import ApplyVouchers from "./apply-vouchers";
import { IVoucher, VoucherDiscountType } from "../../stores/voucher/index.d";

const CartView = observer(({ show, setShowModal }: IModal) => {
  const { pathname } = useLocation();
  const {
    isShopClosed,
    minimumOrderAmount,
    minimumCollectionOrderAmount,
    defaultDeliveryCost,
    orderDeliveryRadius,
    cashPaymentCollection,
    cashPaymentDelivery,
  } = useShopContext();
  const { cartStore, productStore } = useStores();
  const {
    cartItems,
    deliveryMethod,
    setDeliveryMethod,
    setDeliveryAddress,
    setOrderDateTime,
    paymentMethod,
    setPaymentMethod,
    placeOrder,
    clearCartItems,
    cartTotal,
    setCartTotal,
    deliveryCost,
    setDeliveryCost,
    checkDistance,
    deliveryAddress,
    updateCartItemQuantity,
    validateCartItems,
    tableNumber,
    setTableNumber,
    voucherDiscount,
    setVoucherDiscount,
    setAppliedVoucherCode,
  } = cartStore;
  const { products, fetchProducts } = productStore;

  const {
    estimatedTimeForCollection,
    estimatedTimeForDelivery,
    shopPostcode,
    shopTableCount,
  } = useShopContext();
  const { isAuthenticated, isShopUser, isShopAdmin } = useAuth();
  const [cookies, setCookie, removeCookie] = useCookies();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showOrderSummarySection, setShowOrderSummarySection] = useState(true);
  const [showAddressSection, setShowAddressSection] = useState(false);
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const [showOrderSuccess, setShowOrderSuccess] = useState(false);
  const [isAddressFormValid, setIsAddressFormValid] = useState(false);
  const [isPaymentFormValid, setIsPaymentFormValid] = useState(false);
  const [orderId, setOrderId] = useState(0);
  const [orderSuccessMessage, setOrderSuccessMessage] = useState("");
  const [cartSubTotal, setCartSubTotal] = useState(
    cartItems?.reduce(
      (a, b) =>
        a +
        b.Quantity *
          (b.discountForQuantity <= b.Quantity && b.discountPrice
            ? b.discountPrice
            : b.sellPrice) +
        (b.addOnsPrice ?? 0),
      0
    )
  );
  const [voucherApplied, setVoucherApplied] = useState<IVoucher | undefined>();

  const [isCartItemsValid, setCartItemsValid] = useState(false);
  const isTableOrderRoute = pathname.includes("/tbl-orders");
  const isTableOrder = isTableOrderRoute;
  const markShopClosed =
    isShopClosed && !isTableOrderRoute && !isShopAdmin && !isShopUser;

  useEffect(() => {
    if (isTableOrder) {
      setDeliveryMethod(DeliveryMethod.DineIn);
      setPaymentMethod(PaymentMethod.Direct);
    }
  }, [isTableOrder]);

  useEffect(() => {
    if (!products?.length) fetchProducts();
  }, [products, products?.length]);

  useEffect(() => {
    setShowOrderSummarySection(cartItems?.length > 0);
    const subTot = cartItems?.reduce(
      (a, b) =>
        a +
        b.Quantity *
          (b.discountForQuantity <= b.Quantity && b.discountPrice
            ? b.discountPrice
            : b.sellPrice) +
        (b.addOnsPrice ?? 0),
      0
    );
    setCartSubTotal(subTot);
    if (cartItems?.length && products?.length) {
      // The basket stays mounted between openings. Revalidate whenever its
      // contents change so checkout does not remain hidden after products load.
      validateCartItems((isValid: boolean) => setCartItemsValid(isValid));
    } else if (!cartItems?.length) {
      setCartItemsValid(false);
      setVoucherApplied(undefined);
    }
  }, [cartItems, cartItems?.length, products?.length]);

  useEffect(() => {
    const [finalCartSubTotal, discount] = voucherApplied
      ? getDiscountedValue(
          cartSubTotal,
          voucherApplied.discountType,
          voucherApplied.discountValue
        )
      : [cartSubTotal, 0];

    setVoucherDiscount(discount);
    setAppliedVoucherCode(voucherApplied?.voucherCode);
    var delFee =
      voucherApplied &&
      voucherApplied.discountType === VoucherDiscountType.FreeDelivery
        ? 0
        : defaultDeliveryCost;
    setDeliveryCost(delFee);

    setCartTotal(
      deliveryMethod === DeliveryMethod.Delivery
        ? finalCartSubTotal + delFee
        : finalCartSubTotal
    );
  }, [cartSubTotal, deliveryMethod, defaultDeliveryCost, voucherApplied]);

  const handleCheckout = (isLoggedIn?: boolean) => {
    if (!isAuthenticated && !isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    setShowAddressSection(true);
    setShowOrderSummarySection(false);
  };

  const loginCallback = useCallback(() => {
    setShowLoginModal(false);
    if (isTableOrder) handleProceedToPayment();
    else handleCheckout(true);
  }, [isTableOrder]);

  const handleBackFromAddress = () => {
    setShowAddressSection(false);
    setShowOrderSummarySection(true);
  };

  const onAddressDetailsChange = (
    isAddressFormValid: boolean,
    address: IAddress
  ) => {
    setIsAddressFormValid(isAddressFormValid);
    setDeliveryAddress(address);
  };

  const onOrderDateTimeChange = (
    isDateTimeValid: boolean,
    orderDateTime: {
      date: string;
      time: string;
    }
  ) => {
    setIsAddressFormValid(isDateTimeValid);
    setOrderDateTime(orderDateTime);
  };

  const checkDistanceCallback = (dist: IDistanceCheckResponse) => {
    if (!dist) ShowAlert.Error("Please enter valid postcode.");
    else if (dist.distanceInMiles > orderDeliveryRadius)
      ShowAlert.Error(
        "Sorry! Postcode out of our service area. Try collection."
      );
    else {
      setShowAddressSection(false);
      setShowOrderSummarySection(false);
      setShowPaymentSection(true);
    }
  };

  function handleProceedToPayment() {
    if (deliveryMethod == DeliveryMethod.Delivery) {
      checkDistance(
        shopPostcode,
        deliveryAddress?.postcode,
        checkDistanceCallback
      );
    } else {
      setShowAddressSection(false);
      setShowOrderSummarySection(false);
      setShowPaymentSection(true);
    }
  }

  const handleTableOrderCheckout = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    handleProceedToPayment();
  };

  const handleBackFromPayment = () => {
    setShowAddressSection(true);
    setShowOrderSummarySection(false);
    setShowPaymentSection(false);
  };

  const resetCartView = useCallback(() => {
    setShowOrderSummarySection(false);
    setShowAddressSection(false);
    setShowPaymentSection(false);
    setVoucherApplied(undefined);
    clearCartItems();
    if (isTableOrder) {
      setDeliveryMethod(DeliveryMethod.DineIn);
      setPaymentMethod(PaymentMethod.Direct);
    }
  }, []);

  const addPaymentSessionCookie = (sessionId: string, orderId: number) => {
    if (cookies.pyId) {
      removeCookie("pyId");
    }
    if (cookies.pyOrderId) {
      removeCookie("pyOrderId");
    }
    var expire = moment().add(5, "minutes").toDate();
    setCookie("pyId", sessionId, { expires: expire });
    setCookie("pyOrderId", orderId, { expires: expire });
  };

  const placeOrderCallback = useCallback(
    (res: ApiResponse<ICreateOrderResponse>) => {
      if (res.isSuccess && res.data) {
        const { orderId, stripeResponse } = res.data;
        resetCartView();
        setOrderId(orderId);
        setShowOrderSuccess(true);
        setOrderSuccessMessage(res.message);
        ShowAlert.Success(res.message);
        if (stripeResponse?.id) {
          addPaymentSessionCookie(stripeResponse?.id, orderId);
          window.location.href = stripeResponse?.stripeCheckOutUrl;
        }
      } else {
        ShowAlert.Error(
          res.message ??
            "Failed to place order due to some technical error. Please contact support."
        );
      }
    },
    []
  );

  const handleOrderSuccessClose = () => {
    setShowModal(false);
    setShowOrderSuccess(false);
    setOrderId(0);
  };

  const handlePlaceOrder = () => {
    if (markShopClosed) {
      ShowAlert.Error("Sorry, we are not taking orders at the moment.");
      return;
    }
    placeOrder(placeOrderCallback);
  };

  const tableOptions = () => {
    const options = Array.from(
      { length: shopTableCount ?? 50 },
      (_, i) => i + 1
    );
    return (
      <>
        {options.map((value) => (
          <option key={value} value={value} selected={value === tableNumber}>
            {value}
          </option>
        ))}
      </>
    );
  };

  const applyVoucherCallback = useCallback((voucher: IVoucher | undefined) => {
    setVoucherApplied(voucher);
  }, []);

  return (
    <Modal
      show={show}
      onHide={() => handleOrderSuccessClose()}
      centered
      scrollable
      dialogClassName="modal-lg modal-fullscreen-sm-down"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h4>
            {isTableOrder
              ? `Dine-In Order #${tableNumber > 0 ? tableNumber : ""}`
              : "My Basket"}
          </h4>
        </Modal.Title>
      </Modal.Header>
      {cartItems?.length > 0 &&
        (isTableOrder ? (
          <div className="input-group px-2 mt-2">
            <span className="input-group-text px-5 btn-default text-white">
              <FontAwesomeIcon icon={faUtensils} />
            </span>
            <div className="form-floating">
              <select
                className="form-control"
                id="tableNumber"
                name="tableNumber"
                value={tableNumber}
                onChange={(e) => setTableNumber(_.toNumber(e.target.value))}
              >
                <option key="0" value="0" selected={0 === tableNumber}>
                  Choose a table
                </option>
                {tableOptions()}
              </select>
              <label htmlFor="tableNumber">Table Number</label>
            </div>
          </div>
        ) : (
          <>
            <div className="d-flex btn-group" data-toggle="buttons">
              <label
                className={`btn btn-outline-default rounded-0 flex-fill ${
                  DeliveryMethod.Delivery == deliveryMethod ? "active" : ""
                } ${showPaymentSection ? "disabled" : ""}`}
              >
                <input
                  type="radio"
                  name="options"
                  id="option1"
                  autoComplete="off"
                  className="btn-check text-center"
                  checked={DeliveryMethod.Delivery == deliveryMethod}
                  onChange={() => setDeliveryMethod(1)}
                  disabled={markShopClosed}
                />
                Delivery
              </label>
              <label
                className={`btn btn-outline-default rounded-0 flex-fill ${
                  DeliveryMethod.Collection == deliveryMethod ? "active" : ""
                } ${showPaymentSection ? "disabled" : ""}`}
              >
                <input
                  type="radio"
                  name="options"
                  id="option2"
                  autoComplete="off"
                  className="btn-check"
                  checked={DeliveryMethod.Collection == deliveryMethod}
                  onChange={() => setDeliveryMethod(2)}
                  disabled={markShopClosed}
                />
                Collection
              </label>
            </div>
            <div className="d-flex text-center">
              <small className="flex-fill">
                Estimated: {estimatedTimeForDelivery} mins
              </small>
              <small className="flex-fill">
                Estimated: {estimatedTimeForCollection} mins
              </small>
            </div>
          </>
        ))}

      <Modal.Body className={markShopClosed ? "shop-closed" : ""}>
        {!cartItems || cartItems.length <= 0 ? (
          <>
            {showOrderSuccess && orderId > 0 ? (
              <OrderSuccessBanner
                orderId={orderId}
                handleOrderSuccessClose={handleOrderSuccessClose}
                successMessage={orderSuccessMessage}
                isTableOrder={isTableOrder}
              />
            ) : (
              <>
                <div className="h4 text-center mt-4 mb-5">
                  Your
                  <FontAwesomeIcon
                    icon={faShoppingBasket}
                    className="text-default ms-2 me-2"
                  />
                  basket is empty.
                </div>
                <div className="d-flex">
                  <a href="/" className="flex-fill btn-default btn">
                    Shop now
                  </a>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {showOrderSummarySection && (
              <div className="row">
                <div className="col-lg-12 col-md-12">
                  <div>
                    {cartItems && cartItems.length > 0 ? (
                      <>
                        <CartItems
                          cartItems={cartItems}
                          clearCartItems={clearCartItems}
                          updateCartItemQuantity={updateCartItemQuantity}
                        />

                        <div className="mt-2">
                          <ApplyVouchers
                            callback={applyVoucherCallback}
                            orderValue={cartSubTotal}
                          />
                        </div>

                        <div className="mt-3 mb-3">
                          <div className="d-flex justify-content-between">
                            <span>Sub-total</span>
                            <span>{currencyFormatter(cartSubTotal)}</span>
                          </div>

                          <div className="d-flex justify-content-between">
                            <span>Tax</span> <span>{currencyFormatter(0)}</span>
                          </div>

                          {voucherApplied &&
                            voucherDiscount > 0 &&
                            voucherApplied.discountType !=
                              VoucherDiscountType.FreeDelivery && (
                              <div className="d-flex justify-content-between text-success">
                                <span>
                                  Discount
                                  {voucherApplied?.discountType ===
                                    VoucherDiscountType.Percentage &&
                                    `(${voucherApplied.discountValue}%)`}
                                </span>
                                <span>
                                  -{currencyFormatter(voucherDiscount)}
                                </span>
                              </div>
                            )}

                          {deliveryMethod === DeliveryMethod.Delivery && (
                            <div className="d-flex justify-content-between">
                              <span>Delivery</span>{" "}
                              {voucherApplied &&
                              voucherApplied.discountType ===
                                VoucherDiscountType.FreeDelivery ? (
                                <span className="text-success">Free</span>
                              ) : (
                                <span>{currencyFormatter(deliveryCost)}</span>
                              )}
                            </div>
                          )}
                          <div className="d-flex justify-content-between fw-bold">
                            Total
                            <span>{currencyFormatter(cartTotal)}</span>
                          </div>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {showAddressSection && (
              <AddressSection
                deliveryMethod={deliveryMethod}
                onAddressChange={onAddressDetailsChange}
                onOrderDateTimeChange={onOrderDateTimeChange}
              />
            )}

            {showPaymentSection && (
              <Payment
                deliveryMethod={deliveryMethod}
                paymentMode={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                setIsPaymentFormValid={setIsPaymentFormValid}
                cashPaymentForCollection={cashPaymentCollection}
                cashPaymentForDelivery={cashPaymentDelivery}
                isTableOrder={isTableOrder}
              />
            )}

            {showLoginModal && (
              <LoginPage
                show={showLoginModal}
                setShowModal={setShowLoginModal}
                loginCallback={loginCallback}
              ></LoginPage>
            )}
          </>
        )}
      </Modal.Body>
      {isCartItemsValid &&
        cartItems &&
        cartItems?.length > 0 &&
        (markShopClosed ? (
          <Modal.Footer className="d-flex p-0">
            <button
              className="fs-4 text-danger btn btn-default d-flex flex-fill justify-content-center pt-3 pb-3 rounded-0 m-0 text-center"
              disabled
            >
              Sorry! We are closed
            </button>
          </Modal.Footer>
        ) : (deliveryMethod == DeliveryMethod.Delivery &&
            (!minimumOrderAmount || cartSubTotal >= minimumOrderAmount)) ||
          (deliveryMethod == DeliveryMethod.Collection &&
            (!minimumCollectionOrderAmount ||
              cartSubTotal >= minimumCollectionOrderAmount)) ||
          deliveryMethod == DeliveryMethod.DineIn ? (
          <Modal.Footer className="d-flex p-0">
            {showOrderSummarySection &&
              (isTableOrder ? (
                <button
                  className="btn btn-default d-flex flex-fill justify-content-between pt-3 pb-3"
                  type="button"
                  disabled={tableNumber <= 0}
                  onClick={handleTableOrderCheckout}
                >
                  <span>Continue to payment</span>
                  <span>{currencyFormatter(cartTotal)}</span>
                </button>
              ) : (
                <button
                  className="btn btn-default d-flex flex-fill justify-content-between pt-3 pb-3"
                  type="button"
                  onClick={() => handleCheckout()}
                >
                  <span>Proceed to Checkout</span>
                  <span>{currencyFormatter(cartTotal)}</span>
                </button>
              ))}
            {showAddressSection && (
              <>
                <button
                  className="btn btn-secondary pt-3 pb-3"
                  type="button"
                  onClick={handleBackFromAddress}
                >
                  Back
                </button>
                <button
                  className="btn btn-default d-flex flex-fill justify-content-between pt-3 pb-3"
                  type="button"
                  disabled={!isAddressFormValid}
                  onClick={handleProceedToPayment}
                >
                  <span>Continue to payment</span>
                  <span>{currencyFormatter(cartTotal)}</span>
                </button>
              </>
            )}
            {showPaymentSection && (
              <>
                <button
                  className="btn btn-secondary pt-3 pb-3"
                  type="button"
                  onClick={handleBackFromPayment}
                >
                  Back
                </button>
                <button
                  className="btn btn-default d-flex flex-fill justify-content-between pt-3 pb-3"
                  type="button"
                  disabled={!isPaymentFormValid}
                  onClick={handlePlaceOrder}
                >
                  <span>Place order</span>
                  <span>{currencyFormatter(cartTotal)}</span>
                </button>
              </>
            )}
          </Modal.Footer>
        ) : (
          <Modal.Footer className="p-0">
            <p className="alert alert-success text-success flex-fill text-center pt-3 pb-3 rounded-0 m-0">
              Minimum order value should be&nbsp;
              <strong>
                {" "}
                {currencyFormatter(
                  deliveryMethod == DeliveryMethod.Delivery
                    ? minimumOrderAmount
                    : minimumCollectionOrderAmount
                )}
              </strong>
              . Please add items worth&nbsp;
              <strong>
                {currencyFormatter(
                  (deliveryMethod == DeliveryMethod.Delivery
                    ? minimumOrderAmount
                    : minimumCollectionOrderAmount) - cartSubTotal
                )}
              </strong>
              &nbsp;to place this order.
            </p>
          </Modal.Footer>
        ))}
    </Modal>
  );
});

export default CartView;
